'use client';

import React, { useState, useEffect } from 'react';
import IndustryCard from './IndustryCard';
import ComplianceSection from './ComplianceSection';
import BenchmarkSection from './BenchmarkSection';
import ROICalculator from './ROICalculator';
import ExpertConsultation from './ExpertConsultation';

interface Industry {
  id: string;
  name: string;
  icon: string;
  description: string;
  image: string;
  alt: string;
  challenges: string[];
  solutions: string[];
  caseStudyCount: number;
}

interface ComplianceItem {
  standard: string;
  description: string;
  icon: string;
}

interface BenchmarkData {
  category: string;
  industry: number;
  venkaLabs: number;
}

interface Expert {
  id: string;
  name: string;
  title: string;
  specialization: string;
  image: string;
  alt: string;
  experience: string;
  industries: string[];
}

const IndustriesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const industries: Industry[] = [
  {
    id: 'technology-saas',
    name: 'Technology & SaaS',
    icon: 'CodeBracketIcon',
    description: 'Accelerate product development with AI-powered automation, scalable cloud infrastructure, and expert engineering talent for rapid market deployment.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16073ccd5-1766925322121.png",
    alt: 'Modern tech office with developers working on multiple monitors showing code and cloud architecture diagrams',
    challenges: [
    'Rapid scaling requirements with limited technical resources',
    'Complex cloud infrastructure management and optimization',
    'AI/ML integration for product differentiation',
    'Talent acquisition in competitive markets'],

    solutions: [
    'AI agent development for automated workflows',
    'Cloud-native architecture design and implementation',
    'Contract staffing with pre-vetted senior engineers',
    'Microsoft Azure and AWS optimization strategies'],

    caseStudyCount: 12
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    icon: 'HeartIcon',
    description: 'Transform patient care with HIPAA-compliant AI solutions, secure data engineering, and specialized healthcare IT talent for digital health innovation.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_195a6e902-1764648555298.png",
    alt: 'Healthcare professional using tablet with medical data visualization and patient monitoring systems in modern hospital setting',
    challenges: [
    'HIPAA compliance and data security requirements',
    'Legacy system integration with modern platforms',
    'Patient data analytics and predictive modeling',
    'Telehealth infrastructure scalability'],

    solutions: [
    'HIPAA-compliant AI agents for patient engagement',
    'Secure data engineering pipelines for EHR systems',
    'Healthcare IT specialists with compliance expertise',
    'Azure Health Data Services implementation'],

    caseStudyCount: 8
  },
  {
    id: 'bfsi',
    name: 'Banking & Financial Services',
    icon: 'BanknotesIcon',
    description: 'Enhance financial operations with secure AI-driven fraud detection, regulatory-compliant data solutions, and specialized fintech engineering expertise.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17cca8024-1766513023990.png",
    alt: 'Financial trading floor with multiple screens displaying real-time market data, charts, and analytics dashboards',
    challenges: [
    'Regulatory compliance across multiple jurisdictions',
    'Real-time fraud detection and risk management',
    'Legacy core banking system modernization',
    'Customer experience personalization at scale'],

    solutions: [
    'AI-powered fraud detection and prevention systems',
    'Regulatory-compliant data engineering solutions',
    'Fintech specialists with banking domain expertise',
    'Cloud migration strategies for core banking systems'],

    caseStudyCount: 10
  },
  {
    id: 'retail',
    name: 'Retail & E-commerce',
    icon: 'ShoppingCartIcon',
    description: 'Optimize customer experiences with AI-driven personalization, omnichannel data integration, and scalable e-commerce engineering solutions.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1241e8e21-1765081387631.png",
    alt: 'Modern retail store with digital displays showing personalized product recommendations and customers using mobile shopping apps',
    challenges: [
    'Omnichannel inventory management complexity',
    'Real-time personalization at scale',
    'Supply chain visibility and optimization',
    'Peak season infrastructure scalability'],

    solutions: [
    'AI agents for personalized customer engagement',
    'Real-time inventory and supply chain analytics',
    'E-commerce platform specialists and architects',
    'Azure Retail solutions and AWS retail services'],

    caseStudyCount: 15
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: 'CogIcon',
    description: 'Drive operational excellence with IoT-enabled AI solutions, predictive maintenance systems, and specialized industrial engineering talent.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1836d87bc-1764648555662.png",
    alt: 'Smart factory floor with robotic arms, IoT sensors, and workers monitoring production on digital dashboards',
    challenges: [
    'IoT device integration and data management',
    'Predictive maintenance implementation',
    'Supply chain disruption mitigation',
    'Quality control automation'],

    solutions: [
    'IoT-enabled AI agents for predictive maintenance',
    'Industrial data engineering and analytics platforms',
    'Manufacturing IT specialists with OT expertise',
    'Azure IoT and AWS IoT Core implementations'],

    caseStudyCount: 9
  },
  {
    id: 'professional-services',
    name: 'Professional Services',
    icon: 'BriefcaseIcon',
    description: 'Streamline service delivery with AI-powered automation, client data intelligence, and specialized consulting technology solutions.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_139d45c67-1766844396733.png",
    alt: 'Professional consulting team collaborating around conference table with laptops and presentation screens showing business analytics',
    challenges: [
    'Project resource allocation optimization',
    'Client data management and insights',
    'Billing and time tracking automation',
    'Knowledge management and collaboration'],

    solutions: [
    'AI agents for project management automation',
    'Client intelligence and analytics platforms',
    'Professional services technology specialists',
    'Microsoft 365 and Power Platform implementations'],

    caseStudyCount: 11
  }];


  const complianceItems: ComplianceItem[] = [
  {
    standard: 'HIPAA Compliance',
    description: 'Healthcare data protection with encrypted storage, access controls, and audit trails for patient information security.',
    icon: 'ShieldCheckIcon'
  },
  {
    standard: 'SOC 2 Type II',
    description: 'Security, availability, and confidentiality controls audited by independent third parties for enterprise trust.',
    icon: 'DocumentCheckIcon'
  },
  {
    standard: 'GDPR Compliance',
    description: 'European data protection regulations with data subject rights, consent management, and privacy by design.',
    icon: 'GlobeAltIcon'
  },
  {
    standard: 'PCI DSS',
    description: 'Payment card industry security standards for secure transaction processing and cardholder data protection.',
    icon: 'CreditCardIcon'
  },
  {
    standard: 'ISO 27001',
    description: 'Information security management system certification with risk assessment and continuous improvement processes.',
    icon: 'LockClosedIcon'
  },
  {
    standard: 'FINRA Compliance',
    description: 'Financial industry regulatory authority standards for broker-dealer operations and customer protection.',
    icon: 'ScaleIcon'
  }];


  const benchmarkData: BenchmarkData[] = [
  { category: 'Implementation Speed', industry: 65, venkaLabs: 92 },
  { category: 'Cost Efficiency', industry: 58, venkaLabs: 88 },
  { category: 'Quality Score', industry: 72, venkaLabs: 95 },
  { category: 'Client Satisfaction', industry: 68, venkaLabs: 98 },
  { category: 'Innovation Index', industry: 60, venkaLabs: 90 }];


  const experts: Expert[] = [
  {
    id: 'expert-1',
    name: 'Dr. Sarah Chen',
    title: 'Healthcare Technology Lead',
    specialization: 'Healthcare AI & Compliance',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17f8f3366-1763293591953.png",
    alt: 'Professional Asian woman in white medical coat with stethoscope smiling confidently in modern hospital setting',
    experience: '15+ years',
    industries: ['Healthcare', 'Life Sciences']
  },
  {
    id: 'expert-2',
    name: 'Michael Rodriguez',
    title: 'Financial Services Architect',
    specialization: 'Fintech & Regulatory Solutions',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10423f54b-1763296327505.png",
    alt: 'Hispanic male professional in navy suit with confident smile in modern office with financial charts in background',
    experience: '12+ years',
    industries: ['BFSI', 'Insurance']
  },
  {
    id: 'expert-3',
    name: 'Emily Thompson',
    title: 'Retail Technology Director',
    specialization: 'E-commerce & Omnichannel',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8f896f2-1763299689286.png",
    alt: 'Caucasian woman with blonde hair in professional business attire smiling in modern retail technology center',
    experience: '10+ years',
    industries: ['Retail', 'E-commerce']
  }];


  const handleLearnMore = (industryId: string) => {
    if (isHydrated) {
      console.log(`Learning more about industry: ${industryId}`);
    }
  };

  return (
    <div>
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) =>
            <IndustryCard
              key={industry.id}
              industry={industry}
              onLearnMore={handleLearnMore} />

            )}
          </div>
        </div>
      </section>

      <ComplianceSection
        title="Industry Compliance & Security Standards"
        description="Invictosoft maintains rigorous compliance certifications across all major industry regulations, ensuring your data and operations meet the highest security and privacy standards."
        items={complianceItems} />


      <BenchmarkSection
        title="Industry Performance Benchmarks"
        description="See how Invictosoft solutions consistently outperform industry averages across key performance metrics, delivering measurable value to our clients."
        data={benchmarkData} />


      <ROICalculator
        title="Calculate Your Industry ROI"
        description="Discover the potential cost savings, time reduction, and productivity gains your organization can achieve with Invictosoft industry-specific solutions." />


      <ExpertConsultation
        title="Consult with Industry Experts"
        description="Connect with our specialized industry experts who understand your sector's unique challenges and can design tailored solutions for your business needs."
        experts={experts} />

    </div>);

};

export default IndustriesInteractive;